import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import { configService } from 'Services';
import {
    MAX_GLOBAL_CURRENCY,
    MAX_COMMENT_LENGTH,
    MAX_GLOBAL_THRESHOLD,
    MIN_GLOBAL_CURRENCY,
    MIN_GLOBAL_THRESHOLD,
    MIN_GLOBAL_FREE_ENTRY_LENGTH,
    MAX_GLOBAL_FREE_ENTRY_LENGTH,
    FREEENTRY_VALUE,
    getCommaSeparatedNumericValue,
    getCommaSeparatedCurrencyValue,
    handleKeyEvent,
    handleOnClickEvent
} from 'Utils';
import { Button, Loading } from '@brighterion/br-ui';
import { getTranslationValidationHandling } from 'Hooks/query-hooks/helper';
import { RULE_INPUT_PARAMETER_NAMES, RULE_ACTION_TYPE } from 'Common/enums';
import _ from 'lodash';
import Layout from 'Components/Layout';
import PageHeader from 'Components/PageHeader';
import RuleTester from 'Components/AddRulePage/RuleTester';
import { shouldShowRuleTester } from 'Selectors';
import EditRuleInfoOverview from '../EditRuleInfoOverview';
import RuleStatement from '../RuleStatement';
import styles from './EditForm.css';

type EditFormProps = {
    parameterData: UseGetParametersResponse | undefined;
    conclusionFlagListData?: ConclusionListApiGetResponse[];
    disabledInputFields?: DisabledInputFields;
    formRef?: any;
    globalRule: Rule;
    listData?: ListApiGetResponse;
    mutation: any;
    overrideRule?: Rule;
    pageHeader: string;
    ruleId: string;
    rulesType: RulesType;
    selectedRowId?: string;
    subjectIdentifier?: string;
};

const EditForm = ({
    parameterData,
    conclusionFlagListData,
    disabledInputFields,
    formRef,
    globalRule,
    listData,
    mutation,
    overrideRule,
    pageHeader,
    ruleId,
    rulesType,
    selectedRowId,
    subjectIdentifier
}: EditFormProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [openModal, setOpenModal] = useState(false);
    const isTenantOverride = rulesType === 'tenantOverrides';
    const currentRule =
        rulesType === 'merchantOverrides' || rulesType === 'tenantOverrides' || rulesType === 'tenantGroupOverrides'
            ? overrideRule
            : globalRule;
    const validationT = getTranslationValidationHandling(t);
    const handleCancel = () => {
        switch (rulesType) {
            case 'globalRules':
                history.push(`/global-rules?selectedRowId=${selectedRowId}`);
                break;
            case 'merchantOverrides':
                history.push(`/overrides/merchant/${subjectIdentifier}?selectedRowId=${selectedRowId}`);
                break;
            case 'tenantGroupOverrides':
                history.push(`/tenants/tenant-group/${subjectIdentifier}?selectedRowId=${selectedRowId}`);
                break;

            default:
                // tenant overrides
                history.push('/global-rules');
                break;
        }
    };
    const handleSave = (formValues: TemplatedRuleFormValues) => {
        const { lists, comment, actions, ...rest } = formValues;

        actions?.forEach((action) => {
            /** If ACCEPT, REJECT or REVIEW delete value from values */
            if (
                [RULE_ACTION_TYPE.ACCEPT, RULE_ACTION_TYPE.REJECT, RULE_ACTION_TYPE.REVIEW].includes(
                    action.operator as RULE_ACTION_TYPE
                )
            ) {
                action.values = [''];
            }
            /** Convert number values in string[] */
            if (typeof action.values === 'number') {
                action.values = [String(action.values)];
            }
            /** Convert string values in string[] */
            if (typeof action.values === 'string') {
                action.values = [action.values];
            }
        });

        mutation.mutate({
            rules: [
                {
                    id: ruleId,
                    actions,
                    parameters: {
                        ...rest
                    },
                    inputs: currentRule?.inputs,
                    lists,
                    nonTemplated: currentRule?.nonTemplated
                }
            ],
            comments: {
                [ruleId]: comment
            }
        });
    };

    const validate = (formValues: TemplatedRuleFormValues) => {
        const errors: TemplatedRuleFormValues = {};

        Object.keys(formValues).forEach((key: any) => {
            switch (key) {
                case RULE_INPUT_PARAMETER_NAMES.SCORE:
                    if (!formValues[key] && formValues[key] !== 0)
                        errors[key] = validationT('required', { field: key });
                    else if (!Number.isInteger(formValues[key]))
                        errors[key] = validationT('strictInteger', { field: key });
                    else if (
                        formValues[key] > configService.constants.maxGlobalScore ||
                        formValues[key] < configService.constants.minGlobalScore
                    )
                        errors[key] = validationT('valueBetween', {
                            field: key,
                            minValue: configService.constants.minGlobalScore,
                            maxValue: configService.constants.maxGlobalScore
                        });

                    break;

                case RULE_INPUT_PARAMETER_NAMES.THRESHOLD:
                    if (!formValues[key] && formValues[key] !== MIN_GLOBAL_THRESHOLD)
                        errors[key] = validationT('required', { field: key });
                    else if (!Number.isInteger(formValues[key]))
                        errors[key] = validationT('strictInteger', { field: key });
                    else if (formValues[key] > MAX_GLOBAL_THRESHOLD || formValues[key] < MIN_GLOBAL_THRESHOLD)
                        errors[key] = validationT('valueBetween', {
                            field: key,
                            minValue: getCommaSeparatedNumericValue(MIN_GLOBAL_THRESHOLD),
                            maxValue: getCommaSeparatedNumericValue(MAX_GLOBAL_THRESHOLD)
                        });

                    break;

                case RULE_INPUT_PARAMETER_NAMES.CURRENCY:
                    if (!formValues[key] && formValues[key] !== MIN_GLOBAL_CURRENCY)
                        errors[key] = validationT('required', { field: key });
                    else if (formValues[key] > MAX_GLOBAL_CURRENCY || formValues[key] < MIN_GLOBAL_CURRENCY)
                        errors[key] = validationT('valueBetween', {
                            field: key,
                            minValue: getCommaSeparatedCurrencyValue(MIN_GLOBAL_CURRENCY),
                            maxValue: getCommaSeparatedCurrencyValue(MAX_GLOBAL_CURRENCY)
                        });

                    break;

                case RULE_INPUT_PARAMETER_NAMES.COMMENT:
                    if (formValues[key].length > MAX_COMMENT_LENGTH)
                        errors[key] = validationT('maxLength', { field: key, charCount: MAX_COMMENT_LENGTH });

                    break;

                case RULE_INPUT_PARAMETER_NAMES.PAYER_AUTH_STR:
                    if (!formValues[key]) {
                        errors[key] = validationT('required', { field: key });
                    } else if (
                        _.isNull(formValues[key].match(FREEENTRY_VALUE)) &&
                        !_.isEqual(currentRule?.parameters[key], formValues[key])
                    ) {
                        errors[key] = validationT('alphanumeric', { field: key });
                    } else if (
                        (formValues[key].length < MIN_GLOBAL_FREE_ENTRY_LENGTH ||
                            formValues[key].length > MAX_GLOBAL_FREE_ENTRY_LENGTH) &&
                        _.isNull(formValues[key].match(FREEENTRY_VALUE)) // not freeEntry string
                    ) {
                        errors[key] = validationT('lengthBetween', {
                            field: key,
                            minValue: MIN_GLOBAL_FREE_ENTRY_LENGTH,
                            maxValue: MAX_GLOBAL_FREE_ENTRY_LENGTH
                        });
                    }

                    break;

                case RULE_INPUT_PARAMETER_NAMES.LISTS:
                    if (formValues[key]) {
                        errors[key] = []; // lists can have more than one list item

                        const formikLists: Array<RuleLists> = Object.values(formValues[key]);

                        formikLists.forEach((list: RuleLists, index: number) => {
                            if (
                                list.listIds.length === 0 &&
                                !currentRule?.inputs?.find((input) => input.fieldName === list.fieldName)?.readOnly
                            )
                                errors[key][index] = validationT('required', { field: 'list' });
                        });

                        // // if lists don't have any error message, delete the 'lists' key/val from errors
                        if (errors[key].length === 0) delete errors[key];
                    }

                    break;

                default:
                    break;
            }
        });

        return errors;
    };

    /** @note Make sure all the data is available before rendering the Form */
    if (!currentRule) return <Loading />;

    /**
     * @note Remove landlord List Ids values if it's a Network Override.
     *
     * The tenant is not sharing the same List Ids with the landlord,
     * but a merchant will have the same List Ids as the tenant.
     */
    if (isTenantOverride) {
        currentRule.actions?.forEach((action) => {
            if (action.operator === RULE_ACTION_TYPE.ADD_TO_LIST) {
                const listIdExist = (action.values as AddToList).listIds.some((id) => {
                    if (listData?.listIdHashMap[Number(id)]) {
                        return true;
                    }
                    return false;
                });
                if (!listIdExist) {
                    (action.values as AddToList).listIds = [];
                }
            }
        });
    }

    const formikInitialValues: TemplatedRuleFormValues = {
        actions: currentRule.actions,
        ...currentRule.parameters,
        ...(currentRule.lists && { lists: currentRule.lists }),
        comment: ''
    };

    return (
        <Formik
            enableReinitialize
            initialValues={formikInitialValues}
            onSubmit={(formValues) => handleSave(formValues)}
            validate={validate}
            innerRef={formRef}
        >
            {(formikProps) => {
                const { dirty, isValid, isSubmitting, handleSubmit, values } = formikProps;
                const updatedValues = { ...values, ruleId };
                const updatedFormikProps = { ...formikProps, values: updatedValues };
                const disableSave = !dirty || !isValid;

                const submitForm = () => {
                    handleSubmit();
                };

                return (
                    <Layout>
                        <PageHeader>
                            <PageHeader.Left className={styles.titleText}>
                                {pageHeader}
                                {!currentRule?.nonTemplated && (
                                    <span className={styles.ruleTypeText}>{t('DEFAULT RULE')}</span>
                                )}
                            </PageHeader.Left>
                            <PageHeader.Right className={styles.rightButtonContainer}>
                                <Button
                                    disabled={isSubmitting}
                                    dataTestId='editForm-cancel-btn'
                                    variant='tertiary'
                                    size='medium'
                                    onKeyDown={handleKeyEvent(handleCancel)}
                                    onClick={handleOnClickEvent(handleCancel)}
                                >
                                    {t('Cancel')}
                                </Button>

                                <Button
                                    dataTestId='editForm-save-btn'
                                    variant='primary'
                                    size='small'
                                    onClick={handleOnClickEvent(submitForm)}
                                    onKeyDown={handleKeyEvent(submitForm)}
                                    disabled={disableSave || isSubmitting}
                                >
                                    {isSubmitting ? t('Saving...') : t('Save Rule')}
                                </Button>
                            </PageHeader.Right>
                        </PageHeader>
                        <EditRuleInfoOverview
                            currentGlobalRule={globalRule!}
                            currentOverrideRule={overrideRule!}
                            listData={listData}
                            rulesType={rulesType}
                            isSubmitting={formikProps.isSubmitting}
                            openModal={openModal}
                            setOpenModal={setOpenModal}
                            subjectIdentifier={subjectIdentifier}
                        />
                        <RuleStatement
                            currentRule={currentRule!}
                            parameterData={parameterData}
                            listData={listData}
                            mutationError={mutation.error}
                            disabledInputFields={disabledInputFields}
                            conclusionFlagListData={conclusionFlagListData}
                        />
                        {shouldShowRuleTester() && (
                            <RuleTester
                                formikProps={updatedFormikProps}
                                parameterData={parameterData}
                                listData={listData}
                                conclusionFlagListData={conclusionFlagListData}
                                mode='edit'
                                currentRule={currentRule}
                            />
                        )}
                    </Layout>
                );
            }}
        </Formik>
    );
};

export default EditForm;
